export class JavaRuntimeError extends Error {
    javaType: string;

    constructor(javaType: string, message: string) {
        super(message);
        this.javaType = javaType;
    }
}

export class StepLimitExceededError extends Error {
    constructor() {
        super("Execution took too long (possible infinite loop) and was stopped.");
    }
}

export function makeStepGuard(maxSteps: number) {
    let steps = 0;
    return function __step(): void {
        steps++;
        if (steps > maxSteps) throw new StepLimitExceededError();
    };
}

export function fmtDouble(x: number): string {
    if (!Number.isFinite(x)) return x > 0 ? "Infinity" : x < 0 ? "-Infinity" : "NaN";
    if (Number.isInteger(x)) return x.toFixed(1);
    return String(x);
}

export function fmtChar(codeOrChar: number | string): string {
    return typeof codeOrChar === "number" ? String.fromCharCode(codeOrChar) : codeOrChar;
}

export function intDiv(a: number, b: number): number {
    if (b === 0) throw new JavaRuntimeError("ArithmeticException", "/ by zero");
    return Math.trunc(a / b) | 0;
}

export function intMod(a: number, b: number): number {
    if (b === 0) throw new JavaRuntimeError("ArithmeticException", "/ by zero");
    return (a % b) | 0;
}

export function toInt(x: number): number {
    return x | 0;
}

export class ScannerReader {
    private buf: string;
    private pos = 0;

    constructor(input: string) {
        this.buf = input;
    }

    private isWhitespace(ch: string): boolean {
        return ch === " " || ch === "\t" || ch === "\n" || ch === "\r" || ch === "\f";
    }

    private tokenStart(): number {
        let p = this.pos;
        while (p < this.buf.length && this.isWhitespace(this.buf[p])) p++;
        return p;
    }

    private peekToken(): string | null {
        const start = this.tokenStart();
        if (start >= this.buf.length) return null;
        let end = start;
        while (end < this.buf.length && !this.isWhitespace(this.buf[end])) end++;
        return this.buf.slice(start, end);
    }

    private readToken(): string {
        const start = this.tokenStart();
        if (start >= this.buf.length) {
            throw new JavaRuntimeError("NoSuchElementException", "No more input");
        }
        let end = start;
        while (end < this.buf.length && !this.isWhitespace(this.buf[end])) end++;
        this.pos = end;
        return this.buf.slice(start, end);
    }

    next(): string {
        return this.readToken();
    }

    nextInt(): number {
        const tok = this.readToken();
        const n = Number(tok);
        if (!Number.isInteger(n)) {
            throw new JavaRuntimeError("InputMismatchException", `For input string: "${tok}"`);
        }
        return n | 0;
    }

    nextLong(): number {
        return this.nextInt();
    }

    nextDouble(): number {
        const tok = this.readToken();
        const n = Number(tok);
        if (Number.isNaN(n)) {
            throw new JavaRuntimeError("InputMismatchException", `For input string: "${tok}"`);
        }
        return n;
    }

    nextBoolean(): boolean {
        const tok = this.readToken();
        if (tok === "true") return true;
        if (tok === "false") return false;
        throw new JavaRuntimeError("InputMismatchException", `For input string: "${tok}"`);
    }

    hasNext(): boolean {
        return this.peekToken() !== null;
    }

    hasNextInt(): boolean {
        const tok = this.peekToken();
        return tok !== null && /^[+-]?\d+$/.test(tok);
    }

    hasNextLine(): boolean {
        return this.pos < this.buf.length;
    }

    nextLine(): string {
        if (this.pos >= this.buf.length) {
            throw new JavaRuntimeError("NoSuchElementException", "No line found");
        }
        const start = this.pos;
        const idx = this.buf.indexOf("\n", this.pos);
        if (idx === -1) {
            this.pos = this.buf.length;
            return this.buf.slice(start).replace(/\r$/, "");
        }
        const line = this.buf.slice(start, idx).replace(/\r$/, "");
        this.pos = idx + 1;
        return line;
    }

    close(): void {
        // no-op for an in-memory reader
    }
}

export function makeScannerFactory(stdin: string) {
    const reader = new ScannerReader(stdin);
    return () => reader;
}

export function makePrinter() {
    const chunks: string[] = [];
    return {
        print(s: string) {
            chunks.push(s);
        },
        println(s: string) {
            chunks.push(s + "\n");
        },
        getOutput() {
            return chunks.join("");
        },
    };
}
